import {
    TextureLoader,
    RepeatWrapping,
    SRGBColorSpace,
    LinearFilter,
    NearestFilter,
} from 'three'

import loadGLTF from './loadGLTF.js'
import loadHDR from './loadHDR.js'
import loadKTX2 from './loadKTX2.js'
import loadAtlas from './loadAtlas.js'

const textureLoader = new TextureLoader()

const EXT_TYPE = {
    glb: 'gltf', gltf: 'gltf',
    hdr: 'hdr', exr: 'hdr',
    ktx2: 'ktx2', basis: 'ktx2',
    json: 'json',
    buf: 'buffer', bin: 'buffer',
}


export default function assetsPlugin(webgl) {
    const textures = {}
    const objects = {}
    const buffers = {}
    const jsons = {}
    const spritesheets = {}

    const _pending = {}
    let _manifest = null


    loadGLTF.initDRACOLoader()


    function resolveEntry(id) {
        if (!_manifest) return null
        const source = _manifest[id]
        if (!source) return null

        if (source.files) {
            const keys = Object.keys(source.files)
            if (!keys.length) return null
            const entry = source.files[keys[0]]
            if (Array.isArray(entry)) return entry[0]?.url ?? entry[0] ?? null
            return entry?.url ?? entry ?? null
        }

        if (Array.isArray(source)) return source[0]?.url ?? source[0] ?? null
        return source?.url ?? source ?? null
    }


    function detectType(url, opts) {
        if (opts.type) return opts.type
        const ext = url.split('.').pop().split('?')[0].toLowerCase()
        return EXT_TYPE[ext] ?? 'texture'
    }


    async function textureTask({ id, url, opts }) {
        if (url.endsWith('.ktx2') || url.endsWith('.basis')) return ktx2Task({ id, url, opts })

        return new Promise((resolve, reject) => {
            textureLoader.load(url, tex => {
                applyTextureOpts(tex, opts)
                textures[id] = tex
                resolve(tex)
            }, undefined, reject)
        })
    }

    async function ktx2Task({ id, url, opts }) {
        const tex = await loadKTX2(url)
        applyTextureOpts(tex, opts)
        textures[id] = tex
        return tex
    }

    async function gltfTask({ id, url }) {
        const gltf = await loadGLTF(url)
        objects[id] = gltf
        return gltf
    }

    async function hdrTask({ id, url }) {
        const tex = await loadHDR(url)
        textures[id] = tex
        return tex
    }

    async function jsonTask({ id, url }) {
        const res = await fetch(url)
        const data = await res.json()
        jsons[id] = data
        return data
    }

    async function bufferTask({ id, url }) {
        const res = await fetch(url)
        const buf = await res.arrayBuffer()
        buffers[id] = buf
        return buf
    }

    async function spritesheetTask({ id, url, opts }) {
        const texId = 'spritesheet-' + id

        const [json] = await Promise.all([
            jsonTask({ id: texId + '-json', url: url.replace(/\.[^.]+$/, '.json') }),
            textureTask({ id: texId, url, opts: { ...opts, flipY: false } }),
        ])

        const atlas = loadAtlas(json)
        atlas.texture = textures[texId]
        spritesheets[id] = atlas
        textures[id] = textures[texId]

        return atlas
    }

    const tasks = {
        gltf: gltfTask,
        hdr: hdrTask,
        ktx2: ktx2Task,
        json: jsonTask,
        buffer: bufferTask,
        texture: textureTask,
        spritesheet: spritesheetTask,
    }


    async function execLoad(id, opts = {}) {
        const url = resolveEntry(id) ?? id
        const storeId = opts.id ?? id.split('/').pop().replace(/\.[^.]+$/, '')
        const type = detectType(url, opts)
        const task = tasks[type] ?? tasks.texture

        return task({ id: storeId, url, opts })
    }


    function applyTextureOpts(tex, opts = {}) {
        if (opts.srgb) tex.colorSpace = SRGBColorSpace
        if (opts.linear) tex.minFilter = tex.magFilter = LinearFilter
        if (opts.nearest) tex.minFilter = tex.magFilter = NearestFilter
        if (opts.repeat) tex.wrapS = tex.wrapT = RepeatWrapping
        if (opts.flipY !== undefined) tex.flipY = opts.flipY
        tex.needsUpdate = true
    }


    webgl.$assets = {
        textures,
        objects,
        buffers,
        jsons,
        spritesheets,

        get: {
            texture: id => textures[id],
            object: id => objects[id],
            buffer: id => buffers[id],
            json: id => jsons[id],
            spritesheet: id => spritesheets[id],
        },

        load(id, opts = {}) {
            const key = opts.id ?? id
            if (_pending[key]) return _pending[key]
            return _pending[key] = execLoad(id, opts)
        },

        setManifest(manifest) {
            _manifest = manifest
        },
    }
}
